/**
 * Отчёты о падениях приложения — чистые правила (T23, docs/tasks/T23-crash-reports.md).
 * Подключение к окну, приложению и сети — `lib/crashReporter.ts`.
 *
 * ⚠️ НИЧЕГО ЛИЧНОГО (решение владельца 17.10.2026). Отчёт анонимный: ни учётки, ни устройства.
 * Текст и стек чистятся здесь и ещё раз на сервере (`users-service/internal/service/app_crash_service.go`,
 * `ScrubCrashText`) — правила обязаны совпадать.
 *
 * ⚠️ ТОЛЬКО ПРИЛОЖЕНИЕ. Сайт отчётов не шлёт (решение владельца): там ошибки видны в консоли, а поток
 * с сотен вкладок забил бы таблицу.
 */

export type CrashKind = 'js-error' | 'unhandled' | 'previous-launch-crash';

/** Не больше пяти отчётов за запуск (решение владельца 17.10.2026). */
export const CRASH_PER_LAUNCH = 5;
export const CRASH_MESSAGE_MAX = 300;
export const CRASH_STACK_MAX = 4000;

// ── чистка ────────────────────────────────────────────────────────────────

const URL_QUERY = /((?:https?|capacitor):\/\/[^\s?#)"']+)[?#][^\s)"']*/g;
const EMAIL = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const JWT = /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g;
const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const LONG_TOKEN = /[A-Za-z0-9_-]{24,}/g;
const LONG_DIGITS = /\d{6,}/g;

/** Убирает почту, токены, номера записей, длинные числа и query у адресов. */
export function scrubCrashText(s: string): string {
  return s
    .replace(URL_QUERY, '$1')
    .replace(EMAIL, '[email]')
    .replace(JWT, '[token]')
    .replace(UUID, ':id')
    // Длинное имя функции без цифр — не токен.
    .replace(LONG_TOKEN, (m) => (/\d/.test(m) ? '[token]' : m))
    .replace(LONG_DIGITS, '[n]');
}

/** Экран без query и якоря, номера записей — :id. */
export function scrubRoute(path: string): string {
  const cut = path.split(/[?#]/)[0] ?? '';
  return scrubCrashText(cut.trim()).slice(0, 200);
}

// ── отпечаток (только для дедупликации внутри запуска) ────────────────────

/** Тип + сообщение + три верхних кадра без номеров строк. Группы считает сервер — это лишь «не слать одно и то же». */
export function crashFingerprint(kind: CrashKind, message: string, stack: string): string {
  const frames = stack
    .split('\n')
    .map((l) => l.replace(/:\d+(:\d+)?/g, '').trim())
    .filter(Boolean)
    .slice(0, 3);
  return [kind, message.trim(), ...frames].join('\u0000');
}

/** Лимит запуска: не больше `max` отчётов и один отпечаток — один раз. */
export function createCrashLimiter(max = CRASH_PER_LAUNCH) {
  const seen = new Set<string>();
  return {
    take(fp: string): boolean {
      if (seen.size >= max || seen.has(fp)) return false;
      seen.add(fp);
      return true;
    },
  };
}

// ── что считать падением ──────────────────────────────────────────────────

/** Сообщение и стек из того, что бросили. */
export function describeError(err: unknown): { message: string; stack: string } {
  if (err instanceof Error) return { message: err.message || err.name, stack: err.stack ?? '' };
  if (typeof err === 'string') return { message: err, stack: '' };
  try {
    return { message: JSON.stringify(err)?.slice(0, CRASH_MESSAGE_MAX) ?? String(err), stack: '' };
  } catch {
    return { message: String(err), stack: '' };
  }
}

/**
 * Не падение, а шум — не шлём.
 * - Отказ сервера, долетевший до `unhandledrejection` (`ApiError` из `api/client.ts`, простой объект со
 *   `status`): это ответ сервера, его видно в журнале шлюза.
 * - «Script error.» без подробностей — чужой скрипт, разбирать нечего.
 * - «ResizeObserver loop …» — безвредное предупреждение браузера.
 */
export function isCrashNoise(err: unknown, message: string): boolean {
  if (err && typeof err === 'object' && !(err instanceof Error) && typeof (err as { status?: unknown }).status === 'number') {
    return true;
  }
  if (/^Script error\.?$/i.test(message.trim())) return true;
  if (/ResizeObserver loop/i.test(message)) return true;
  return false;
}

// ── признак «прошлый запуск не завершился» ────────────────────────────────

/**
 * Флаг в localStorage (решение владельца — не Preferences: это новый нативный плагин в 1.1.2).
 * `running` — приложение на экране; `clean` — ушло в фон, страница закрыта или перезагружается намеренно.
 * Если при старте флаг `running` — прошлый запуск оборвался на экране: упал процесс WebView или приложение.
 * Убийство в фоне падением не считается — флаг уже `clean`.
 */
export const LAUNCH_KEY = 'onebilim.launch';

export type LaunchState = { state: 'running' | 'clean'; version: string; build: string };

type KV = Pick<Storage, 'getItem' | 'setItem'>;

export function readLaunch(store: KV): LaunchState | null {
  try {
    const raw = store.getItem(LAUNCH_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<LaunchState>;
    if (v.state !== 'running' && v.state !== 'clean') return null;
    return { state: v.state, version: String(v.version ?? ''), build: String(v.build ?? '') };
  } catch {
    return null;
  }
}

export function writeLaunch(store: KV, s: LaunchState): void {
  try {
    store.setItem(LAUNCH_KEY, JSON.stringify(s));
  } catch {
    // Хранилище недоступно — признак просто не сработает, падать из-за него нельзя.
  }
}

/**
 * Старт: вернуть прошлый запуск, если он оборвался, и пометить текущий `running`.
 * Версию прошлого запуска берём из флага — после обновления приложения текущая уже другая.
 */
export function startLaunch(store: KV, version: string, build: string): LaunchState | null {
  const prev = readLaunch(store);
  writeLaunch(store, { state: 'running', version: version || prev?.version || '', build: build || prev?.build || '' });
  return prev?.state === 'running' ? prev : null;
}

/**
 * Состояние флага по событию `appStateChange` плагина `@capacitor/app`. На iOS `isActive=false` приходит на
 * `willResignActive` — приложение потеряло активность, но ещё не ушло в фон: открыт переключатель приложений, шторка,
 * системное окно. Отсюда приложение могут смахнуть, не дав ему уйти в фон, — без `clean` здесь такое смахивание
 * выглядело бы падением (проверено 18.10 на симуляторе 18.1: системное окно → закрытие → отчёта нет). Возврат
 * активности — снова `running`: падение на активном экране по-прежнему ловится.
 */
export function launchStateForActive(isActive: boolean): LaunchState['state'] {
  return isActive ? 'running' : 'clean';
}

/** Смена состояния без смены версии. */
export function markLaunch(store: KV, state: LaunchState['state']): void {
  const prev = readLaunch(store);
  writeLaunch(store, { state, version: prev?.version ?? '', build: prev?.build ?? '' });
}

/**
 * Намеренная перезагрузка или уход со страницы — не падение. Звать перед `location.reload()` и
 * `location.replace()`: иначе при следующем запуске флаг `running` выглядел бы оборванным запуском.
 * Сверх этого флаг снимает `pagehide` (`lib/crashReporter.ts`).
 *
 * ⚠️ Живёт здесь, а не в `crashReporter.ts`: его зовёт `api/client.ts`, а `crashReporter` сам импортирует
 * `api/client` — кольцо импортов.
 */
export function markCleanExit(): void {
  try {
    if (readLaunch(window.localStorage)) markLaunch(window.localStorage, 'clean');
  } catch {
    /* хранилище недоступно — признак просто не сработает */
  }
}
